import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
// Service
import { BroadcastService, CONTENT_LOADER_EVENT, ToasterType, ReactiveFormUtils } from '@cv-invision/core';
import { ExternalApiService } from '../../../../../services/external-api.service';
import { ViewsService } from '../../../../../../services/views.service';
import { LayoutRenderService } from '../../../../services/layout-render.service';
// Model
import { LayoutComponentBroadcast, LayoutRenderLayoutItem } from '../../../../models/cv-layout-render.model';
import { GeneralEventType } from '../../../models/component.events';
import { PUBLIC_RECORD_SELECTED } from '../../../../../../models/enums.model';
import { GeneralPropertyDataOptions } from '../../../models/component-options/general-property-data.options';

@Component({
	selector: 'cv-schema-render-general40-property-data',
	templateUrl: './general-property-data.component.html',
	styleUrls: ['./general-property-data.component.scss']
})
export class GeneralPropertyDataComponent implements OnInit, OnDestroy {
	@Input() data: any;
	@Input() layoutNode = new LayoutRenderLayoutItem();

	options = new GeneralPropertyDataOptions();
	path = null;
	isSavingInProcess = false;
	formControlNamesPassthrough = ['waterServices', 'sewerServices', 'electricalServices', 'fuelServices'];
	PUBLIC_RECORD_MESSAGES = {
		multiple: '(Select one Property from the multiple records results)',
		single: '(Autofilled from Public Records)'
	}

	get isEditing(): boolean {
		return this.layoutService.activateEditableUX;
	}

	get isPristine(): boolean {
		return (this.viewsService.form as FormGroup).pristine;
	}

	get viewGseFields(): boolean {
		return this.layoutService.extraData.permissions.viewGseFields;
	}

	get viewPropertyCollectorFields(): boolean {
		return this.layoutService.extraData.permissions.viewPropertyDataCollector;
	}

	get addressEditable(): boolean {
		return this.layoutService.extraData.permissions.editAddress;
	}

	get availableLenders(): any {
		return this.viewsService.lendersAvailables?.data || [];
	}

	get fungibleStatus(): string {
		let txt = 'deactivated';

		if (this.data.order.gseFungible) {
			txt = 'active'
		}

		return txt;
	}

	get hasAvailableLenders(): boolean {
		return this.data.order.lenderSelectionEnable;
	}

	get form(): FormGroup {
		return this.viewsService.form;
	}

	get formIsValid(): boolean {
		let response = false;

		const address = ReactiveFormUtils.getFormControl(this.form, 'InspectionReport.property.address');

		if (address.valid) {
			response = true;
		}

		return response;
	}

	protected atComponentDestroy = new Subject();

	constructor(
		protected broadcast: BroadcastService,
		protected viewsService: ViewsService,
		protected externalApi: ExternalApiService,
		protected layoutService: LayoutRenderService
	) {
		//
	}
	ngOnDestroy(): void {
		this.atComponentDestroy.complete();
		this.atComponentDestroy.unsubscribe();

	}

	ngOnInit(): void {
		this.path = this.data._Path;
		this.options = {...this.options, ...this.layoutNode.options};

		this.layoutService.componentBroadcast
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((broadcastData: LayoutComponentBroadcast) => {
				if (broadcastData.eventType === 'PUBLIC_RECORD') {
					this.saveAndUpdate(broadcastData.metaData.orderID, broadcastData.metaData.publicRecordID);
				}

				if (broadcastData.eventType === 'PUBLIC_RECORDS_READY') {
					if ('publicRecords' in broadcastData.metaData) {
						this.data.publicRecords = broadcastData.metaData.publicRecords;
					}
				}
			});
	}

	/**
	 * Get the changes and save the to the API
	 */
	async saveChanges(showToaster = true): Promise<void> {
		return new Promise(async (resolve, reject) => {
			this.isSavingInProcess = true;

			let eventData = {
				type: 'danger',
				title: 'API ERROR',
				message: ''
			};

			const dirtyObj = {};
			const updatedPaths = {};
			this.getUpdates(this.viewsService.form, dirtyObj, updatedPaths);

			const updates = [];
			for (const path in updatedPaths) {
				updates.push({
					path,
					value: updatedPaths[path]
				});
			}

			if (updates.length) {
				const mediaResponse = await this.externalApi.patchDocument(this.viewsService.order.id, updates, this.viewsService.etag).toPromise();

				if (mediaResponse.body.succeed) {
					eventData = {
						type: 'success',
						title: 'Data Updated',
						message: ''
					};

					this.layoutService.componentBroadcast.next({
						eventType: GeneralEventType.UPDATE_DATA,
						jsonPath: this.path
					});

				} else {
					eventData.message = mediaResponse.body.message;
				}

				if (showToaster) {
					this.broadcast.broadcast({
						name: ToasterType.TOASTER,
						event: eventData
					});
				}

			}

			this.isSavingInProcess = false;
			resolve();
		})
	}

	/**
	 * Recursive function that genereates the Dirty-only object & the updated full path object.
	 * After getting the "field", the function mark the field as pristine.
	 *
	 * @param formItem FormGroup | FormArray | FormControl
	 * @param updatedValuesObject Any writable object {}
	 * @param updatedPaths Any writable object {}
	 * @param name formcontrolName
	 * @param trailingPath Path of the read object
	 * @param passthrough Flag to tell the function to print the object instead of deep read it
	 *
	 * @REFACTOR Replace for @core/ReactiveFormUtils
	 */
	private getUpdates(
		formItem: FormGroup | FormArray | FormControl,
		updatedValuesObject: any,
		updatedPaths: any,
		name?: string,
		trailingPath?: string,
		passthrough?: boolean
	) {
		if (formItem instanceof FormControl || passthrough) {
			if ((name && formItem.dirty) || passthrough) {
				updatedValuesObject[name] = formItem.value;
				updatedPaths[`${trailingPath}${name}`] = formItem.value;
				formItem.markAsPristine();
			}
		} else {
			for (const formControlName in formItem.controls) {
				if (Object.prototype.hasOwnProperty.call(formItem.controls, formControlName)) {
					const formControl = formItem.controls[formControlName];
					let nPath = '';
					if (trailingPath) {
						nPath = `${trailingPath}.`;
					}

					if (formControl instanceof FormControl) {
						this.getUpdates(formControl, updatedValuesObject, updatedPaths, formControlName, nPath);

					} else if (
						formControl instanceof FormArray &&
						formControl.dirty &&
						formControl.controls.length > 0
					) {
						let pass = false;
						if (this.formControlNamesPassthrough.indexOf(formControlName) > -1) {
							pass = true;
						} else {
							nPath = `${nPath}${formControlName}`;
						}

						updatedValuesObject[formControlName] = [];
						this.getUpdates(formControl, updatedValuesObject[formControlName], updatedPaths, formControlName, nPath, pass);

					} else if (formControl instanceof FormGroup && formControl.dirty) {
						if (isNaN(parseInt(formControlName))) {
							nPath = `${nPath}${formControlName}`;
						} else {
							nPath = `${nPath}[${formControlName}]`;
							nPath = nPath.replace('.[', '[');
						}
						updatedValuesObject[formControlName] = {};
						this.getUpdates(formControl, updatedValuesObject[formControlName], updatedPaths, null, nPath);
					}
				}
			}
		}
	}

	/**
	 * Save changes of property data and update the order
	 *
	 * @param orderID string
	 * @param publicRecordID string
	 *
	 * */

	async saveAndUpdate(orderID: string, publicRecordID: string) {
		this.broadcast.broadcast({
			name: CONTENT_LOADER_EVENT,
			event: {
				isSpinnerOn: true,
				isLoader: true
			}
		});

		await this.saveChanges(false);
		await this.externalApi.selectPublicRecordByOrderId(orderID, publicRecordID).toPromise();

		this.broadcast.broadcast({
			name: PUBLIC_RECORD_SELECTED,
			event: {}
		});

		setTimeout(() => {
			this.layoutService.componentBroadcast.next({
				eventType: GeneralEventType.UPDATE_DATA,
				jsonPath: this.path
			});
		}, 500);
	}

}
